import { Payment, Restrictions } from 'payments-lib-types';
import { filter, is, pluck } from 'ramda';
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';

import { useCommonStore } from '../commonStore';
import { REASON_FOR_UPDATING_USER } from '../commonStore/slices/auth/helpers';
import { createSelectors } from '../helpers';
import { AnalyticsEventName } from '@/api/analytics';
import {
  clearBonusCode,
  DepositBonusCode,
  setBonusCode,
  updateBonusSettings,
  User,
} from '@/api/auth';
import { BonusType, CMSBonus } from '@/api/cms/bonuses';
import { DepositStep } from '@/components/Deposit/helpers';
import { sendEventToAnalyticsFn } from '@/helpers/analytics';
import { filterBonusesByGroups } from '@/helpers/bonuses';
import { getCurrentCurrencyData } from '@/helpers/common';
import { CLIENT_ENVS } from '@/helpers/constants';
import {
  amountWithCurrencySymbol,
  getCurrencyName,
  MAP_COUNTRY_TO_CURRENCY,
} from '@/helpers/currency';
import { ThemeMode } from '@/helpers/enums';
import { fetchPaymentMethods } from '@/helpers/payments';
import { unmaskAmount } from '@/helpers/validators';

export interface PaymentMethod extends Payment.Method {
  internalId: string;
  savedProfileDetails?: Payment.SavedProfile;
  isPrevUsed?: boolean;
}

export interface DepositStorageState {
  step: DepositStep;
  currentAmount: number | string;
  currentAmountWithCurrency: number | string;
  originalAmount: number | string;
  activeThemeMode: ThemeMode | undefined;
  defaultAmountRestrictions: Restrictions | undefined;
  currentPaymentMethod: PaymentMethod | null;
  pendingBonuses: boolean;
  cashierBonuses: CMSBonus[];
  canIssueBonuses: boolean;
  paymentMethods: PaymentMethod[] | undefined;
  pendingPaymentMethods: boolean;
  previouslyUsedPaymentMethods: PaymentMethod[];
  pendingUpdateBonusSettings: boolean;
  pendingBonusCode: boolean;
  selectedBonus: CMSBonus | null;
  setCurrentAmount: (amount: number | string) => void;
  setOriginalAmount: (amount: number | string) => void;
  fetchPaymentMethods: () => void;
  handleRemoveBonusCode: () => void;
  handleSetBonusCode: (values: DepositBonusCode) => void;
  handleChangeReceiveBonuses: () => void;
  setCanIssueBonuses: (canIssueBonuses: boolean) => void;
  setSelectedBonus: (id: CMSBonus | null) => void;
  setCashierBonuses: (cmsBonuses: CMSBonus[], user: User) => void;
  setDefaultAmountRestrictions: () => void;
  setPreviouslyUsedPaymentMethods: (
    previouslyUsedPaymentMethods: PaymentMethod[]
  ) => void;
}

const useDepositStoreBase = create<DepositStorageState>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      step: DepositStep.SELECT_WALLET,
      currentAmount: '',
      currentAmountWithCurrency: '',
      originalAmount: '',
      activeThemeMode: ThemeMode.Light,
      defaultAmountRestrictions: undefined,
      currentPaymentMethod: null,
      pendingBonuses: false,
      cashierBonuses: [],
      canIssueBonuses: true,
      paymentMethods: undefined,
      pendingPaymentMethods: false,
      previouslyUsedPaymentMethods: [],
      pendingUpdateBonusSettings: false,
      pendingBonusCode: false,
      selectedBonus: null,
      setCurrentAmount: (currentAmount: number | string) => {
        const { user, currencies } = useCommonStore.getState();

        const currencyConversion = (amount: number, currency: string) => {
          return getCurrentCurrencyData(amount, currency, currencies).currency;
        };

        const currentAmountWithCurrency = (() => {
          if (!user?.currency) return 0;

          const amount = unmaskAmount(currentAmount);
          const currency = currencyConversion(amount, user.currency);

          return amountWithCurrencySymbol(
            getCurrencyName(currency),
            currentAmount
          );
        })();
        set({ currentAmount, currentAmountWithCurrency });
      },
      setOriginalAmount: (originalAmount: number | string) => {
        set({ originalAmount });
      },
      fetchPaymentMethods: async () => {
        try {
          set({ pendingPaymentMethods: true });
          const { user } = useCommonStore.getState();
          if (!user?.currency) return;

          const { data } = await fetchPaymentMethods({
            currency: user.currency,
          });
          const paymentMethods = data.map((paymentMethod) => ({
            ...paymentMethod,
            internalId: paymentMethod.id,
          }));
          set({ paymentMethods, pendingPaymentMethods: false });
        } catch (error) {
          console.log(error);
          set({ pendingPaymentMethods: false });
        }
      },
      handleRemoveBonusCode: async () => {
        const { fetchUser, user } = useCommonStore.getState();

        set({ pendingBonusCode: true });

        const { error } = await clearBonusCode();
        if (error) return;

        sendEventToAnalyticsFn({
          name: AnalyticsEventName.MODAL__DEPOSIT__PROMO_CODE_FORM__CANCEL,
          attributes: {
            userId: user?.id,
          },
        });

        await fetchUser(REASON_FOR_UPDATING_USER.DEPOSIT);

        set({ selectedBonus: null, pendingBonusCode: false });
      },
      handleSetBonusCode: async (values: DepositBonusCode) => {
        const { fetchUser, user } = useCommonStore.getState();

        set({ pendingBonusCode: true });

        const { error } = await setBonusCode(values);

        sendEventToAnalyticsFn({
          name: AnalyticsEventName.MODAL__DEPOSIT__PROMO_CODE_FORM__ADD,
          attributes: {
            promocode: values.depositBonusCode,
            userId: user?.id,
          },
        });

        if (error) return;

        await fetchUser(REASON_FOR_UPDATING_USER.DEPOSIT);

        set({ pendingBonusCode: false });
      },
      handleChangeReceiveBonuses: async () => {
        const { fetchUser, user } = useCommonStore.getState();
        const { canIssueBonuses } = get();

        set({ pendingUpdateBonusSettings: true });

        const { error } = await updateBonusSettings(!canIssueBonuses);
        set({ pendingUpdateBonusSettings: false });
        if (error) return;

        await fetchUser(REASON_FOR_UPDATING_USER.DEPOSIT);

        if (!canIssueBonuses) {
          sendEventToAnalyticsFn({
            name: AnalyticsEventName.MODAL__DEPOSIT__TURN_ON_BONUSES__CLICK,
            attributes: {
              userId: user?.id,
            },
          });
        } else {
          sendEventToAnalyticsFn({
            name: AnalyticsEventName.MODAL__DEPOSIT__TURN_OFF_BONUSES__CLICK,
            attributes: {
              userId: user?.id,
            },
          });
        }
      },
      setCanIssueBonuses: (canIssueBonuses: boolean) =>
        set({ canIssueBonuses }),
      setSelectedBonus: (selectedBonus: CMSBonus | null) =>
        set({ selectedBonus }),
      setCashierBonuses: (cmsBonuses, user) => {
        const cashierBonuses = filterBonusesByGroups(
          cmsBonuses,
          'cashierToggle',
          BonusType.ALL,
          user
        );

        set({ cashierBonuses });
      },
      setDefaultAmountRestrictions: () => {
        const { countryCode, user } = useCommonStore.getState();
        const { paymentMethods } = get();

        if (!paymentMethods || !countryCode) return;

        const restrictionsList = paymentMethods.map(({ termsOfService }) => {
          return termsOfService.restrictions;
        });
        const minAmountValueList: number[] = filter(
          is(Number),
          pluck('minAmountValue', restrictionsList)
        );
        const maxAmountValueList: number[] = filter(
          is(Number),
          pluck('maxAmountValue', restrictionsList)
        );

        const defaultAmountRestrictions = {
          minAmountValue: minAmountValueList?.length
            ? Math.min(...minAmountValueList)
            : null,
          maxAmountValue: maxAmountValueList?.length
            ? Math.max(...maxAmountValueList)
            : null,
          amountCurrencyCode:
            user?.currency || MAP_COUNTRY_TO_CURRENCY[countryCode],
        };
        set({ defaultAmountRestrictions });
      },
      setPreviouslyUsedPaymentMethods: (
        previouslyUsedPaymentMethods: PaymentMethod[]
      ) => {
        set({ previouslyUsedPaymentMethods });
      },
    })),
    { name: 'useDepositStore', enabled: CLIENT_ENVS.APP_ENV === 'development' }
  )
);

export const useDepositStore = createSelectors(useDepositStoreBase);
